import { customerApiClient } from './customer-api-client';
import type {
  CustomerModificationRound,
  CustomerQuotationRevisionSummary,
  CustomerRevisionDecisionPayload,
} from '@/types/customer-modification.types';

/**
 * D-131 M7 — the customer's modification-request routes (readiness §8.1).
 *
 * Every route is location-scoped and tenant-scoped server-side (D-130): an
 * out-of-scope or nonexistent job answers 404, indistinguishable from a missing
 * one. Nothing here can reach an internal entry, an owner or a verdict — the
 * server never serialises them for a customer.
 */
const base = (jobId: string) => `/customer-portal/jobs/${jobId}/modification-rounds`;

export const customerModificationService = {
  /** The request currently in progress, or `null`. */
  current: (jobId: string): Promise<CustomerModificationRound | null> =>
    customerApiClient
      .get<CustomerModificationRound | null>(`${base(jobId)}/current`)
      .then((r) => r.data ?? null),

  /** Every request on the job, newest first. */
  history: (jobId: string): Promise<CustomerModificationRound[]> =>
    customerApiClient
      .get<CustomerModificationRound[]>(base(jobId))
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  /** The quotations sent for the job, by public identity only. */
  revisions: (jobId: string): Promise<CustomerQuotationRevisionSummary[]> =>
    customerApiClient
      .get<CustomerQuotationRevisionSummary[]>(`/customer-portal/jobs/${jobId}/quotation-revisions`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  /** T7 — answer the service team's question. */
  reply: (jobId: string, roundId: string, message: string): Promise<CustomerModificationRound> =>
    customerApiClient
      .post<CustomerModificationRound>(`${base(jobId)}/${roundId}/reply`, { message })
      .then((r) => r.data),

  /** T17 — add to the open request. */
  addToRequest: (
    jobId: string,
    roundId: string,
    message: string,
  ): Promise<CustomerModificationRound> =>
    customerApiClient
      .post<CustomerModificationRound>(`${base(jobId)}/${roundId}/add`, { message })
      .then((r) => r.data),

  /**
   * T14 / T15 / T16 — decide on the revised quotation, bound to the revision
   * the customer is looking at. APPROVE is multipart and carries the D-129 Work
   * Order (number, description, PDF as `workOrderDocument`).
   */
  decide: (
    jobId: string,
    roundId: string,
    payload: CustomerRevisionDecisionPayload,
  ): Promise<CustomerModificationRound> => {
    const url = `${base(jobId)}/${roundId}/decision`;
    if (payload.decision === 'APPROVE') {
      const fd = new FormData();
      fd.append('decision', 'APPROVE');
      fd.append('revisionId', payload.revisionId);
      fd.append('workOrderNumber', payload.workOrderNumber);
      fd.append('workOrderDescription', payload.workOrderDescription);
      fd.append('workOrderDocument', payload.file);
      return customerApiClient
        .post<CustomerModificationRound>(url, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    }
    const body =
      payload.decision === 'REJECT'
        ? { decision: 'REJECT', revisionId: payload.revisionId, message: payload.message }
        : {
            decision: 'REQUEST_MODIFICATION',
            revisionId: payload.revisionId,
            requestText: payload.requestText,
          };
    return customerApiClient.post<CustomerModificationRound>(url, body).then((r) => r.data);
  },
};
